'use client';

import React, { useEffect, useRef } from 'react';

export default function VisionSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const elements = sectionRef?.current?.querySelectorAll('.reveal-on-scroll');
    if (!elements) return;
    elements?.forEach((el) => el?.classList?.add('hidden-initially'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const accentTags = ['For Us · لنا', 'By Us · بنا', 'Fisabilillah · في سبيل الله', 'Open Toolkit'];

  return (
    <section
      ref={sectionRef}
      id="vision"
      className="relative overflow-hidden grain-overlay"
      style={{
        background: 'linear-gradient(180deg, #0F1E18 0%, #1C2B25 40%, #0F1E18 100%)',
        paddingTop: 'clamp(64px, 10vw, 112px)',
        paddingBottom: 'clamp(64px, 10vw, 112px)',
      }}
    >
      {/* Atmospheric blobs */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none"
        style={{
          width: '80vw',
          height: '60vh',
          background: 'radial-gradient(ellipse at center, rgba(44,74,62,0.8) 0%, rgba(28,43,37,0.3) 50%, transparent 100%)',
          filter: 'blur(80px)',
          opacity: 0.6,
          top: '-20%',
        }}
      />
      <div
        className="blob-1 absolute pointer-events-none"
        style={{
          width: 'clamp(200px, 30vw, 400px)',
          height: 'clamp(200px, 30vw, 400px)',
          top: '20%',
          right: '-10%',
          background: 'radial-gradient(circle, rgba(139,105,20,0.2) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8 relative z-10">

        {/* Section label + accent tags */}
        <div className="reveal-on-scroll stagger-1 mb-10 flex flex-wrap justify-center gap-3">
          <span
            className="font-mono text-xs tracking-widest uppercase px-4 py-2 rounded"
            style={{
              color: '#B38A2A',
              background: 'rgba(139,105,20,0.15)',
              border: '1px solid rgba(139,105,20,0.3)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            The Vision · الرؤية
          </span>
          {accentTags?.map((tag) => (
            <span
              key={tag}
              className="font-mono tracking-wider px-3 py-1.5 rounded"
              style={{
                color: '#7EC8A0',
                background: 'rgba(44,74,62,0.25)',
                border: '1px solid rgba(44,74,62,0.4)',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.6rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Main quote — full width, centered */}
        <div className="text-center max-w-4xl mx-auto">
          <h2
            className="reveal-on-scroll stagger-2 font-heading text-white"
            style={{
              fontSize: 'clamp(1.8rem, 5.5vw, 4.5rem)',
              lineHeight: 1.05,
              letterSpacing: '-0.03em',
              fontWeight: 400,
              fontStyle: 'italic',
            }}
          >
            "A garden of caretakers,{' '}
            <span className="text-shimmer">no walls,</span>
            {' '}an open toolkit."
          </h2>

          <p
            className="reveal-on-scroll stagger-3 font-body mt-6 max-w-2xl mx-auto leading-relaxed"
            style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)', color: 'rgba(245,240,232,0.5)', lineHeight: 1.75 }}
          >
            Anywhere we can come to understand, stand on and work from. For us, by us,{' '}
            <span className="font-mono" style={{ color: '#B38A2A', fontFamily: 'var(--font-mono)' }}>fisabilillah</span>
            {' '}— in the way of Allah.
          </p>
        </div>

        {/* Three pillars */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-px mt-16"
          style={{ border: '1px solid rgba(255,255,255,0.06)' }}
        >
          {[
            {
              icon: '🌱',
              title: 'For Us',
              tag: 'Community-Grown',
              tagColor: '#7EC8A0',
              tagBg: 'rgba(44,74,62,0.3)',
              body: 'Built from within the community, by those who have walked the path. Not outsider-designed solutions but insider-grown understanding.',
              delay: 'stagger-2',
            },
            {
              icon: '🤲',
              title: 'By Us',
              tag: 'Founder-Documented',
              tagColor: '#B38A2A',
              tagBg: 'rgba(139,105,20,0.2)',
              body: 'The founder began writing after coming into deen, documenting spiritual experiences, building vocabulary from the Islamic lexicon to name what was being experienced.',
              delay: 'stagger-3',
            },
            {
              icon: '✦',
              title: 'Fisabilillah',
              tag: 'In the Way of Allah',
              tagColor: '#7EC8A0',
              tagBg: 'rgba(44,74,62,0.3)',
              body: 'In the way of Allah. Every component, every resource, every connection — not as an end in itself, but as a sign pointing back to the One.',
              delay: 'stagger-4',
            },
          ]?.map((pillar) => (
            <div
              key={pillar?.title}
              className={`reveal-on-scroll ${pillar?.delay} p-8 md:p-10 flex flex-col gap-4`}
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              <span style={{ fontSize: 28 }}>{pillar?.icon}</span>
              <div>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <h3
                    className="font-heading text-white"
                    style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)', fontWeight: 600, letterSpacing: '-0.015em' }}
                  >
                    {pillar?.title}
                  </h3>
                  <span
                    className="font-mono tracking-wider px-2 py-0.5 rounded"
                    style={{
                      color: pillar?.tagColor,
                      background: pillar?.tagBg,
                      border: `1px solid ${pillar?.tagColor}40`,
                      fontSize: '0.55rem',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {pillar?.tag}
                  </span>
                </div>
                <p
                  className="font-body leading-relaxed"
                  style={{ fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)', color: 'rgba(245,240,232,0.45)', lineHeight: 1.7 }}
                >
                  {pillar?.body}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Founder's note */}
        <div
          className="reveal-on-scroll stagger-3 mt-12 p-8 md:p-10 rounded-card"
          style={{
            background: 'rgba(139,105,20,0.08)',
            border: '1px solid rgba(139,105,20,0.2)',
          }}
        >
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div
              className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(139,105,20,0.2)', border: '1px solid rgba(139,105,20,0.35)' }}
            >
              <span style={{ color: '#B38A2A', fontSize: 18 }}>✦</span>
            </div>
            <div>
              <p
                className="font-heading text-white mb-4"
                style={{ fontSize: 'clamp(0.95rem, 2.2vw, 1.3rem)', lineHeight: 1.55, fontStyle: 'italic', fontWeight: 400 }}
              >
                "The Haal Project is a framework for Islamic spiritual research and community caretaking — a collection of ideas acquired in submission toward Allah, linking people to resources not as ends in themselves, but as Ayat — signs pointing back to the One."
              </p>
              <span
                className="font-mono text-xs tracking-widest uppercase"
                style={{ color: 'rgba(245,240,232,0.3)', fontFamily: 'var(--font-mono)' }}
              >
                The Founder — Documenting the Path
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}